/* eslint-disable no-unused-vars */
import { AllDomainEvents } from "@invoiceddd/domain";
import { Context, Data, Effect, Layer, Queue, Option, Fiber } from "effect";

/**
 * Event bus errors
 * @since 0.1.3
 */
export class EventBusError extends Data.TaggedError("@/EventBus/EventBusError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly eventType?: string;
}> {}

/**
 * Event handler function type for domain events.
 * @template T - The domain event type
 * @since 0.1.3
 * @example
 * const handler: EventHandler<InvoiceCreated> = (event) => 
 *   Effect.log(`Invoice created: ${event.invoiceNumber}`)
 */
export type EventHandler<T extends AllDomainEvents> = (event: T) => Effect.Effect<void, never>;

/**
 * Event subscription for a specific event type.
 * Represents an active subscription to events of a particular type.
 * @since 0.1.3
 */
export interface EventSubscription {
  readonly eventType: string;
  readonly handler: EventHandler<AllDomainEvents>;
  readonly subscriptionId: string;
}

/**
 * Event bus interface for publishing domain events and managing subscriptions.
 * 
 * Provides type-safe event publishing and subscription capabilities for domain events.
 * Supports concurrent event processing and graceful error handling.
 * 
 * @interface IEventBus
 * @since 0.1.3
 * @see {@link EventBusLive} for default implementation
 * @example
 * ```typescript
 * import { EventBus } from '@invoiceddd/application';
 * 
 * const eventBus = yield* EventBus;
 * await eventBus.start();
 * 
 * // Subscribe to events
 * const subscriptionId = await eventBus.subscribe(
 *   'InvoiceCreated',
 *   (event) => Effect.log(`Received: ${event.invoiceNumber}`)
 * );
 * 
 * // Publish an event
 * await eventBus.publish(invoiceCreatedEvent);
 * ```
 */
export interface IEventBus {
  /**
   * Publish a domain event to all subscribers.
   * @template E - The domain event type
   * @param event - The domain event to publish
   * @returns Effect that completes when event is queued for processing
   * @since 0.1.3
   * @example
   * await eventBus.publish(invoiceCreatedEvent)
   */
  readonly publish: <E extends AllDomainEvents>(event: E) => Effect.Effect<void, EventBusError>;

  /**
   * Subscribe to events of a specific type.
   * @template E - The domain event type to subscribe to
   * @param eventType - The event type string to subscribe to
   * @param handler - The handler function to process events
   * @returns Effect that resolves to subscription ID for later unsubscribing
   * @since 0.1.3
   * @example
   * const subscriptionId = await eventBus.subscribe(
   *   'InvoiceCreated',
   *   (event) => Effect.log(`Processing: ${event.invoiceNumber}`)
   * )
   */
  readonly subscribe: <E extends AllDomainEvents>(
    eventType: string,
    handler: EventHandler<E>
  ) => Effect.Effect<string, EventBusError>; // Returns subscription ID

  /**
   * Unsubscribe from events using subscription ID.
   * @param subscriptionId - The subscription ID returned from subscribe()
   * @returns Effect that completes when unsubscribed
   * @since 0.1.3
   * @example
   * await eventBus.unsubscribe(subscriptionId)
   */
  readonly unsubscribe: (subscriptionId: string) => Effect.Effect<void, EventBusError>;

  /**
   * Start the event processing loop.
   * Must be called before publishing or subscribing to events.
   * @returns Effect that completes when event processing starts
   * @since 0.1.3
   * @example
   * await eventBus.start()
   */
  readonly start: Effect.Effect<void, EventBusError>;

  /**
   * Stop the event processing and cleanup.
   * Gracefully shuts down the event bus and clears all subscriptions.
   * @returns Effect that completes when shutdown is complete
   * @since 0.1.3
   * @example
   * await eventBus.stop()
   */
  readonly stop: Effect.Effect<void, EventBusError>;
}

/**
 * Context tag for EventBus service.
 * Used for dependency injection in Effect-TS.
 * @since 0.1.3
 * @see {@link IEventBus}
 */
export const EventBus = Context.GenericTag<IEventBus>("@/EventBus");

/**
 * Default implementation of EventBus using Effect.Queue for in-memory event processing.
 *
 * This implementation provides:
 * - Type-safe event publishing and subscription
 * - Automatic event routing to interested subscribers  
 * - Concurrent event processing with error isolation
 * - Graceful startup and shutdown
 * - Fire-and-forget event publishing semantics
 *
 * @since 0.1.3
 * @see {@link IEventBus}
 * @example
 * ```typescript
 * import { EventBusLive } from '@invoiceddd/application';
 * 
 * // Use in Effect layer composition
 * const AppLayer = Layer.mergeAll(
 *   EventBusLive,
 *   // ... other layers
 * );
 * ```
 */
export const EventBusLive = Layer.effect(
  EventBus,
  Effect.gen(function* () {
    // Create an unbounded queue for events
    const eventQueue = yield* Queue.unbounded<AllDomainEvents>();

    // Map to store subscriptions by subscription ID
    const subscriptions = new Map<string, EventSubscription>();

    // Map to store subscriptions by event type for efficient lookup
    const subscriptionsByType = new Map<string, Set<EventSubscription>>();

    // Track if the event bus is running
    let isRunning = false;
    let processingFiber: Option.Option<Fiber.Fiber<void, never>> = Option.none();

    const implementation: IEventBus = {
      publish: <E extends AllDomainEvents>(event: E) =>
        Effect.gen(function* () {
          if (!isRunning) {
            return yield* Effect.fail(
              new EventBusError({
                message: "EventBus is not running. Call start() first.",
                eventType: event.type,
              })
            );
          }

          yield* Queue.offer(eventQueue, event).pipe(
            Effect.mapError(
              cause =>
                new EventBusError({
                  message: `Failed to publish event: ${event.type}`,
                  eventType: event.type,
                  cause,
                })
            )
          );
        }),

      subscribe: <E extends AllDomainEvents>(eventType: string, handler: EventHandler<E>) =>
        Effect.gen(function* () {
          // eslint-disable-next-line no-undef
          const subscriptionId = crypto.randomUUID();

          const subscription: EventSubscription = {
            eventType,
            handler: handler as EventHandler<AllDomainEvents>,
            subscriptionId,
          };

          // Store subscription by ID
          subscriptions.set(subscriptionId, subscription);

          // Store subscription by event type
          if (!subscriptionsByType.has(eventType)) {
            subscriptionsByType.set(eventType, new Set());
          }
          subscriptionsByType.get(eventType)?.add(subscription);

          yield* Effect.log(`[EventBus] Registered subscriber for event: ${eventType}`);

          return subscriptionId;
        }).pipe(
          Effect.mapError(
            cause =>
              new EventBusError({
                message: `Failed to subscribe to event: ${eventType}`,
                eventType,
                cause,
              })
          )
        ),

      unsubscribe: (subscriptionId: string) =>
        Effect.gen(function* () {
          const subscription = subscriptions.get(subscriptionId);

          if (!subscription) {
            return yield* Effect.fail(
              new EventBusError({
                message: `Subscription not found: ${subscriptionId}`,
              })
            );
          }

          // Remove from subscriptions map
          subscriptions.delete(subscriptionId);

          // Remove from event type map
          const typeSubscriptions = subscriptionsByType.get(subscription.eventType);
          if (typeSubscriptions) {
            typeSubscriptions.delete(subscription);
            if (typeSubscriptions.size === 0) {
              subscriptionsByType.delete(subscription.eventType);
            }
          }

          yield* Effect.log(`[EventBus] Unsubscribed from event: ${subscription.eventType}`);
        }).pipe(
          Effect.mapError(
            cause =>
              new EventBusError({
                message: `Failed to unsubscribe: ${subscriptionId}`,
                cause,
              })
          )
        ),

      start: Effect.gen(function* () {
        if (isRunning) {
          yield* Effect.log("[EventBus] Already running");
          return;
        }

        isRunning = true;

        // Start the event processing fiber immediately with forkDaemon
        const processEvents = Effect.gen(function* () {
          while (isRunning) {
            const event = yield* Queue.take(eventQueue);

            // Get subscribers for this event type
            const eventSubscriptions = subscriptionsByType.get(event.type) || new Set();

            if (eventSubscriptions.size === 0) {
              // Log when no subscribers are found (useful for debugging)
              yield* Effect.log(`[EventBus] No subscribers for event type: ${event.type}`);
              continue;
            }

            // Process all subscribers for this event type
            const handlers = Array.from(eventSubscriptions).map(subscription =>
              Effect.sync(() => subscription.handler(event)).pipe(
                Effect.flatMap(effect => effect),
                Effect.tapError(error => {
                  return Effect.logError(
                    `[EventBus] Handler error for event ${event.type}:`,
                    error
                  );
                }),
                Effect.catchAll(() => {
                  // Log handler errors but don't fail the entire event processing
                  return Effect.void;
                })
              )
            );

            // Run all handlers concurrently
            yield* Effect.all(handlers, { concurrency: "unbounded" });

            yield* Effect.log(
              `[EventBus] Successfully processed event: ${event.type} with ${handlers.length} handlers`
            );
          }
        }).pipe(Effect.forever, Effect.forkDaemon);

        const fiber = yield* processEvents;
        processingFiber = Option.some(fiber);

        yield* Effect.log("[EventBus] Started event processing");
      }).pipe(
        Effect.mapError(
          cause =>
            new EventBusError({
              message: "Failed to start EventBus",
              cause,
            })
        )
      ),

      stop: Effect.succeed(void 0).pipe(
        Effect.flatMap(() =>
          Effect.gen(function* () {
            if (!isRunning) {
              yield* Effect.log("[EventBus] Already stopped");
              return;
            }

            isRunning = false;

            // Stop the processing fiber if it exists
            if (Option.isSome(processingFiber)) {
              yield* Fiber.interrupt(processingFiber.value);
            }

            // Clear all subscriptions
            subscriptions.clear();
            subscriptionsByType.clear();

            processingFiber = Option.none();

            yield* Effect.log("[EventBus] Stopped event processing");
          })
        ),
        Effect.mapError(
          cause =>
            new EventBusError({
              message: "Failed to stop EventBus",
              cause,
            })
        )
      ),
    };

    return EventBus.of(implementation);
  })
);
